import { Injectable } from '@angular/core';

import { select, Store } from '@ngrx/store';

import { PatientPartialState } from './patient.reducer';
import { patientQuery } from './patient.selectors';
import * as actions from './patient.actions';

import {
  PatientDetailRequest,
  PharmacyFacetRequest,
  SureFitRequest,
  CoverageDetail,
  PatientDetailsObj,
  BRNPayload,
  Alliance,
  IdCardDetails,
} from '@cigna/chcp/patient/shared/util';
import {
  ProviderNetworkVerificationRequest,
  PathwellNetworkVerificationRequest,
  FnsNetworkVerificationRequest,
} from '@cigna/chcp/shared/provider-data-access';
import { AuthFacade } from '@cigna/chcp/auth/data-access';
import { take, map } from 'rxjs/operators';
import { combineLatest } from 'rxjs';
import { ServiceError } from '@cigna/chcp/shared/util';

@Injectable()
export class PatientFacade {
  pageStage$ = this.store$.pipe(select(patientQuery.getPageStage));
  error$ = this.store$.pipe(select(patientQuery.getError));
  searchRows$ = this.store$.pipe(select(patientQuery.getSearchRows), take(1));
  results$ = this.store$.pipe(select(patientQuery.getResults));
  resultsFiltered$ = this.store$.pipe(select(patientQuery.getResultsFiltered));
  resultFilter$ = this.store$.pipe(select(patientQuery.getResultFilter));
  resultFilterOptions$ = this.store$.pipe(
    select(patientQuery.getResultFilterOptions),
  );
  productDetails$ = this.store$.pipe(select(patientQuery.getProductDetails));
  isMultipleMatch$ = this.store$.pipe(select(patientQuery.isMultipleMatch));
  isSingleNonExactMatch$ = this.store$.pipe(
    select(patientQuery.isSingleNonExactMatch),
  );
  patientRowDetailLoading$ = this.store$.pipe(
    select(patientQuery.getPatientRowDetailLoading),
  );
  patientEstimateDetailsLoading$ = this.store$.pipe(
    select(patientQuery.getPatientEstimateDetailsLoading),
  );
  patientEstimateResultLoading$ = this.store$.pipe(
    select(patientQuery.getPatientEstimateResultLoading),
  );
  patientRowInactiveLoading$ = this.store$.pipe(
    select(patientQuery.getPatientRowInactiveLoading),
  );
  displayPatientDetails$ = this.store$.pipe(
    select(patientQuery.displayPatientDetails),
  );
  patientDetail$ = this.store$.pipe(select(patientQuery.getPatientDetail));
  patientAsOfDate$ = this.store$.pipe(select(patientQuery.getPatientAsOfDate));
  benefitReportDetails$ = this.store$.pipe(
    select(patientQuery.getBenefitReportDetails),
  );
  getAlliances$ = this.store$.pipe(select(patientQuery.getAlliances));
  coverageDetail$ = this.store$.pipe(select(patientQuery.getCoverageDetail));
  ccfBenefit$ = this.store$.pipe(select(patientQuery.getCcfBenefit));
  estimateType$ = this.store$.pipe(select(patientQuery.estimateType));
  costEstimationDetail$ = this.store$.pipe(
    select(patientQuery.getCostEstimationDetail),
  );
  serviceProcedures$ = this.store$.pipe(
    select(patientQuery.getServiceProcedures),
  );
  patientEstimateCostResult$ = this.store$.pipe(
    select(patientQuery.getCostEstimationResult),
  );
  submitEstimateError = this.store$.pipe(
    select(patientQuery.submitEstimateError),
  );
  cobDetails$ = this.store$.pipe(select(patientQuery.getCobDetails));
  frequencyAgePlanDetails$ = this.store$.pipe(
    select(patientQuery.getFrequencyDetails),
  );
  notificationDetail$ = this.store$.pipe(
    select(patientQuery.getNotificationDetails),
  );
  pharmacyBenefitDetails$ = this.store$.pipe(
    select(patientQuery.getPharmacyBenefitDetails),
  );
  serviceContacts$ = this.store$.pipe(select(patientQuery.getServiceContacts));
  planLevelAccum$ = this.store$.pipe(select(patientQuery.getPlanLevelAccum));
  primaryCareDetails$ = this.store$.pipe(
    select(patientQuery.getPrimaryCareDetails),
  );
  subscriberDetail$ = this.store$.pipe(
    select(patientQuery.getSubscriberDetail),
  );
  getPatientRouteData$ = this.store$.pipe(
    select(patientQuery.getPatientRouteData),
  );
  eligibilityDate$ = this.store$.pipe(select(patientQuery.getEligibilityDate));
  isImpersonator$ = this.authFacade.isImpersonator$;
  getBenefitTags$ = this.store$.pipe(select(patientQuery.getBenefitTags));
  getAmInNetworkStatus$ = this.store$.pipe(
    select(patientQuery.getAmInNetworkStatus),
  );
  getAccumulatedServiceData$ = this.store$.pipe(
    select(patientQuery.getAccumulatedServiceData),
  );
  getPatientCompositeKey$ = this.store$.pipe(
    select(patientQuery.getPatientCompositeKey),
  );
  getMaternityBenefit$ = this.store$.pipe(
    select(patientQuery.getMaternityBenefit),
  );
  getVisionServices$ = this.store$.pipe(select(patientQuery.getVisionServices));
  getInfertilityServices$ = this.store$.pipe(
    select(patientQuery.getInfertilityServices),
  );
  getHealthWellness$ = this.store$.pipe(select(patientQuery.getHealthWellness));
  getProviderGroups$ = this.store$.pipe(select(patientQuery.getProviderGroups));
  getProviders$ = this.store$.pipe(select(patientQuery.getProviders));
  getProviderDetails$ = this.store$.pipe(
    select(patientQuery.getProviderDetails),
  );
  getRawProviderData$ = this.store$.pipe(
    select(patientQuery.getUnsortedProviderDetails),
  );
  getInOutNetworkDetails$ = this.store$.pipe(
    select(patientQuery.getInOutNetworkDetails),
  );
  idCardDetails$ = this.store$.pipe(select(patientQuery.idCardDetails));
  idCardError$ = this.store$.pipe(select(patientQuery.getIdCardError));
  dentalOfficeDetails$ = this.store$.pipe(
    select(patientQuery.dentalOfficeDetails),
  );
  sureFitDetails$ = this.store$.pipe(select(patientQuery.sureFitDetails));
  planLevelTags$ = this.store$.pipe(select(patientQuery.getPlanLevelTags));
  ohpTin$ = this.store$.pipe(select(patientQuery.ohpTin));
  precertUrl$ = this.store$.pipe(select(patientQuery.getPrecertUrl));
  isAccountRestricted$ = combineLatest([
    this.authFacade.isRestrictedImpersonator$,
    this.store$.pipe(select(patientQuery.isAccountRestricted)),
  ]).pipe(
    map(
      ([impersonatorRestricted, restricted]) =>
        !!(restricted && impersonatorRestricted),
    ),
  );
  sureFitUrl$ = this.store$.pipe(select(patientQuery.getSureFitUrl));
  pharmacyBenefit$ = this.store$.pipe(select(patientQuery.getPharmacyBenefit));
  medAmInNetworkSelectedTin$ = this.store$.pipe(
    select(patientQuery.getMedAmInNetworkSelectedTin),
  );

  visionOnlyDetails$ = this.store$.pipe(
    select(patientQuery.getVisionOnlyDetails),
  );

  constructor(
    private store$: Store<PatientPartialState>,
    private authFacade: AuthFacade,
  ) {}

  setOhpTin(tin: any) {
    this.store$.dispatch(new actions.SetOhpTin(tin));
  }
  searchPatients(data: any) {
    this.store$.dispatch(new actions.SearchPatients(data));
  }
  resetSearch() {
    this.store$.dispatch(new actions.ResetSearch());
  }
  newSearch() {
    this.store$.dispatch(new actions.NewSearch());
  }
  coverageStatusChange(val: string) {
    this.store$.dispatch(new actions.ChangeResultFilter(val));
  }
  modifySearch() {
    this.store$.dispatch(new actions.ModifySearch());
  }
  onInactiveMemberSearch(data: any) {
    this.store$.dispatch(new actions.InactiveMemberSearch(data));
  }
  getPatientDetails(data: PatientDetailRequest) {
    this.store$.dispatch(new actions.GetPatientDetail(data));
  }
  setToInitialState() {
    this.store$.dispatch(new actions.ResetSearch());
  }
  updateAmInNetworkStatus(status: boolean, accumListCheckCSN = false) {
    this.store$.dispatch(
      new actions.GetAmInNetworkStatus({ status, accumListCheckCSN }),
    );
  }
  getProviderGroup(tin: string) {
    this.store$.dispatch(new actions.GetProviderGroup(tin));
  }
  getProviders(id: string[]) {
    this.store$.dispatch(new actions.GetProviders(id));
  }
  getInOutNetworkDetails(data: {
    payload: ProviderNetworkVerificationRequest;
    isIndemnity: boolean;
    tin: string;
  }) {
    this.store$.dispatch(
      new actions.GetProviderDetails({
        provId: data.payload ? data.payload.provId : '',
        tin: data.tin ? data.tin : '',
      }),
    );
    this.store$.dispatch(new actions.GetInOutNetworkDetails(data));
  }

  getPathwellIFPInOutNetworkDetails(data: {
    payload: PathwellNetworkVerificationRequest;
    isIndemnity: boolean;
    hasIFP: boolean;
  }) {
    this.store$.dispatch(
      new actions.GetProviderDetails({
        provId: data.payload ? data.payload.provId : '',
        tin: data.payload.tin ? data.payload.tin : '',
      }),
    );
    this.store$.dispatch(new actions.GetPathwellIFPInOutNetworkDetails(data));
  }

  getFnsInOutNetworkDetails(data: {
    payload: FnsNetworkVerificationRequest;
    isIndemnity: boolean;
  }) {
    this.store$.dispatch(
      new actions.GetProviderDetails({
        provId: data.payload ? data.payload.provId : '',
        tin: data.payload.tin ? data.payload.tin : '',
      }),
    );
    this.store$.dispatch(new actions.GetFNSInOutNetworkDetails(data));
  }

  resetProviderData(key: string) {
    this.store$.dispatch(new actions.ResetProviderData(key));
  }
  fetchFacetDetails(data: PharmacyFacetRequest) {
    this.store$.dispatch(new actions.GetPharmacyFacets(data));
  }
  openAccordionDetails(key: string) {
    this.store$.dispatch(new actions.OpenAccordionDetails(key));
  }
  resetErrors() {
    this.store$.dispatch(new actions.ResetErrors());
  }
  resetDetails() {
    this.store$.dispatch(new actions.ResetDetails());
  }
  resetSubmitEstimateError() {
    this.store$.dispatch(new actions.ResetSubmitEstimateError());
  }
  updateCompositeKey(key: string) {
    this.store$.dispatch(new actions.UpdateCompositeKey(key));
  }
  setEstimateType(type: string) {
    this.store$.dispatch(new actions.SetEstimateType(type));
  }
  getCostEstimationDetail(key: string, patientType: string, network: string) {
    this.store$.dispatch(
      new actions.GetCostEstimatorDetail(key, patientType, network),
    );
  }
  getServiceProcedures(payload: any) {
    this.store$.dispatch(new actions.GetServiceProcedures(payload));
  }
  resetServiceProcedures() {
    this.store$.dispatch(new actions.ResetServiceProcedures());
  }
  postEstimateCost(payload: any) {
    this.store$.dispatch(new actions.PatientEstimateCost(payload));
  }
  resetEstimateCostResult() {
    this.store$.dispatch(new actions.ResetPatientEstimateCost());
  }
  fetchSureFitDetails(data: SureFitRequest) {
    this.store$.dispatch(new actions.GetSureFitDetails(data));
  }
  getPrecertUrl() {
    this.store$.dispatch(new actions.GetPrecertUrl());
  }
  hydratePatientDataFromCoverages(payload: {
    patient: PatientDetailsObj;
    coverage: CoverageDetail;
    alliance: Alliance;
    compositeKey: string;
    reference?: BRNPayload;
  }) {
    this.store$.dispatch(new actions.HydratePatientDataFromCoverages(payload));
  }

  hydrateIdCardFromCoverages({
    idCardDetails,
    error,
  }: {
    idCardDetails: IdCardDetails | null;
    error: ServiceError | null;
  }) {
    this.store$.dispatch(
      new actions.HydrateIdCardFromCoverages({ idCardDetails, error }),
    );
  }
}
